import React, { useState, useEffect } from 'react';
import {
  X,
  Search,
  Clock,
  FileText,
  Camera,
  PenTool,
  Check,
  Loader2,
} from 'lucide-react';

/**
 * CheckInModal - Modal for checking in a student
 * @param {boolean} isOpen - Whether the modal is open
 * @param {Function} onClose - Callback to close the modal
 * @param {Function} onSubmit - Callback when form is submitted
 * @param {Array} students - List of students to select from
 * @param {Object} preSelectedStudent - Pre-selected student (optional)
 * @param {boolean} isLoading - Loading state
 */
const CheckInModal = ({
  isOpen,
  onClose,
  onSubmit,
  students = [],
  preSelectedStudent = null,
  isLoading = false,
}) => {
  const [selectedStudent, setSelectedStudent] = useState(preSelectedStudent);
  const [searchQuery, setSearchQuery] = useState('');
  const [checkInTime, setCheckInTime] = useState(
    new Date().toISOString().slice(0, 16)
  );
  const [notes, setNotes] = useState('');
  const [showPhotoCapture, setShowPhotoCapture] = useState(false);
  const [showSignature, setShowSignature] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedStudent(preSelectedStudent);
      setSearchQuery('');
      setCheckInTime(new Date().toISOString().slice(0, 16));
      setNotes('');
      setShowPhotoCapture(false);
      setShowSignature(false);
    }
  }, [isOpen, preSelectedStudent]);

  if (!isOpen) return null;

  // Filter students based on search
  const filteredStudents = students.filter((student) => {
    const fullName = `${student.firstName} ${student.lastName}`.toLowerCase();
    return fullName.includes(searchQuery.toLowerCase());
  });

  // Get initials for avatar
  const getInitials = (student) => {
    const first = student?.firstName?.[0] || '';
    const last = student?.lastName?.[0] || '';
    return (first + last).toUpperCase();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedStudent) return;

    onSubmit({
      studentId: selectedStudent.id,
      checkInTime: new Date(checkInTime).toISOString(),
      notes: notes.trim() || undefined,
      method: 'manual',
    });
  };

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
    setSearchQuery('');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">
            {preSelectedStudent ? 'Check In Student' : 'Quick Check In'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-8rem)]">
          <form id="checkin-form" onSubmit={handleSubmit}>
            {/* Student Selection - Only show if no pre-selected student */}
            {!preSelectedStudent && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Student
                </label>
                <div className="relative">
                  <Search
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search students..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                {/* Student List */}
                {searchQuery && (
                  <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {filteredStudents.length === 0 ? (
                      <p className="p-3 text-sm text-gray-500 text-center">
                        No students found
                      </p>
                    ) : (
                      filteredStudents.map((student) => (
                        <button
                          key={student.id}
                          type="button"
                          onClick={() => handleStudentSelect(student)}
                          className="w-full flex items-center space-x-3 p-3 hover:bg-purple-50 transition-colors text-left"
                        >
                          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-purple-700 font-medium text-xs">
                              {getInitials(student)}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {student.firstName} {student.lastName}
                            </p>
                            <p className="text-xs text-gray-500">
                              {student.className || 'No class'}
                            </p>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Selected Student Display */}
            {selectedStudent && (
              <div className="mb-4 p-3 bg-purple-50 rounded-lg flex items-center space-x-3">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-purple-700 font-semibold">
                    {getInitials(selectedStudent)}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">
                    {selectedStudent.firstName} {selectedStudent.lastName}
                  </p>
                  <p className="text-sm text-gray-500">
                    {selectedStudent.className || 'No class assigned'}
                  </p>
                </div>
                {!preSelectedStudent && (
                  <button
                    type="button"
                    onClick={() => setSelectedStudent(null)}
                    className="text-sm text-purple-600 hover:text-purple-700"
                  >
                    Change
                  </button>
                )}
              </div>
            )}

            {/* Check-in Time */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock size={16} className="inline mr-1" />
                Check-in Time
              </label>
              <input
                type="datetime-local"
                value={checkInTime}
                onChange={(e) => setCheckInTime(e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            </div>

            {/* Notes */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FileText size={16} className="inline mr-1" />
                Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about this check-in..."
                rows={3}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Photo Capture Option */}
            <div className="mb-4">
              <button
                type="button"
                onClick={() => setShowPhotoCapture(!showPhotoCapture)}
                className="flex items-center space-x-2 px-3 py-2 min-h-[44px] text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                <Camera size={16} />
                <span>Capture Photo</span>
                <span className="text-xs text-gray-400">(Coming Soon)</span>
              </button>
              {showPhotoCapture && (
                <div className="mt-2 p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 text-center">
                  <Camera size={32} className="mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500">
                    Photo capture will be available soon
                  </p>
                </div>
              )}
            </div>

            {/* Parent Signature Option */}
            <div className="mb-4">
              <button
                type="button"
                onClick={() => setShowSignature(!showSignature)}
                className="flex items-center space-x-2 px-3 py-2 min-h-[44px] text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                <PenTool size={16} />
                <span>Parent Signature</span>
                <span className="text-xs text-gray-400">(Coming Soon)</span>
              </button>
              {showSignature && (
                <div className="mt-2 p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 text-center">
                  <PenTool size={32} className="mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500">
                    Digital signature will be available soon
                  </p>
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 min-h-[44px] text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="checkin-form"
            disabled={!selectedStudent || isLoading}
            className="flex items-center space-x-2 px-4 py-2 min-h-[44px] text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span>Checking In...</span>
              </>
            ) : (
              <>
                <Check size={16} />
                <span>Check In</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckInModal;
